import React, { useEffect, useState, useCallback } from "react";
import { ListChecks, Play, Timer, CircleDot, Heart, Clock, Hourglass, Flame, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API, axios, URGENCY_LEVELS, urgencyMeta, sessionStatus, answerStatusOf, formatElapsed, waitingLabel, daysSince } from "../lib/planner";
import { AppNav } from "../components/planner/AppNav";
import { FocusMode } from "../components/planner/FocusMode";

const GROUPS = [
  { key: "high", label: "Urgent", hint: "Bring these before Him first" },
  { key: "medium", label: "Important", hint: "Steady, ongoing prayer" },
  { key: "low", label: "When you can", hint: "Hold these gently" },
];

const PriorityCard = ({ p, meta, onUrgency, onStart, onAnswered, onPrayNow }) => {
  const sStatus = sessionStatus(p);
  const aStatus = answerStatusOf(p);
  const scriptureRef = [p.scripture, p.chapter, p.verse].filter(Boolean).join(" ");
  return (
    <div
      className="rounded-2xl border border-[#E5E1D8] bg-white p-5 shadow-[0_4px_24px_-6px_rgba(44,53,41,0.06)]"
      style={{ borderLeft: `4px solid ${meta.color}` }}
      data-testid={`priority-${p.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-serif text-xl leading-tight text-ink">{p.prayer_point || "Untitled prayer"}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-xs text-ink-muted">
            <span>{p.day} · {p.week_key}</span>
            {scriptureRef && <span>{scriptureRef}</span>}
            {p.duration_minutes > 0 && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{p.duration_minutes}m</span>}
            {p.time_of_prayer && <span>{p.time_of_prayer}</span>}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span
              className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                daysSince(p.created_at) >= 7 ? "bg-[#F3E3DE] text-[#B4553F]" : "bg-clay-panel text-ink-muted"
              }`}
              data-testid={`waiting-${p.id}`}
            >
              <Hourglass className="h-3 w-3" /> {waitingLabel(p.created_at)}
            </span>
            {aStatus === "in_progress" && <span className="rounded-full bg-clay-soft px-2 py-0.5 text-[11px] text-[#7a6a4f]">Praying over</span>}
            {aStatus === "unanswered" && <span className="rounded-full bg-clay-panel px-2 py-0.5 text-[11px] text-ink-muted">Awaiting</span>}
            {sStatus === "not_started" && (
              <span className="flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] text-ink-muted ring-1 ring-[#E5E1D8]"><CircleDot className="h-3 w-3" />Not started</span>
            )}
            {sStatus === "in_progress" && (
              <span className="flex items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-[11px] font-medium text-brand"><Timer className="h-3 w-3" />In progress · {formatElapsed(p.started_at)}</span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="flex items-center gap-1" data-testid={`urgency-controls-${p.id}`}>
            {URGENCY_LEVELS.map((u) => (
              <button
                key={u.key}
                onClick={() => onUrgency(p, u.key)}
                data-testid={`urgency-${u.key}-${p.id}`}
                title={`${u.label} urgency`}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-all duration-300 ${
                  (p.urgency || "medium") === u.key ? "text-white" : "bg-clay-panel text-ink-muted hover:bg-clay-soft"
                }`}
                style={(p.urgency || "medium") === u.key ? { backgroundColor: u.color } : {}}
              >
                {u.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => onPrayNow(p)} data-testid={`pray-now-${p.id}`} className="flex items-center gap-1 rounded-full bg-brand px-3 py-1.5 text-xs font-medium text-white transition-colors duration-300 hover:bg-brand-hover">
              <Sparkles className="h-3 w-3" /> Pray now
            </button>
            {sStatus === "not_started" && !p.completed && (
              <button onClick={() => onStart(p)} data-testid={`start-${p.id}`} className="flex items-center gap-1 rounded-full border border-[#E5E1D8] px-3 py-1.5 text-xs font-medium text-ink-soft transition-colors duration-300 hover:bg-clay-panel">
                <Play className="h-3 w-3" /> Start
              </button>
            )}
            <button onClick={() => onAnswered(p)} data-testid={`answered-${p.id}`} className="flex items-center gap-1 rounded-full border border-brand/40 px-3 py-1.5 text-xs font-medium text-brand transition-colors duration-300 hover:bg-clay-soft">
              <Heart className="h-3 w-3" /> Answered
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function PrioritiesPage() {
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState(() => localStorage.getItem("sanctuary_priorities_sort") || "urgency");
  const [focusEntry, setFocusEntry] = useState(null);

  useEffect(() => { localStorage.setItem("sanctuary_priorities_sort", sortMode); }, [sortMode]);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/prayers/active`);
    setPrayers(r.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setUrgency = async (p, key) => {
    await axios.put(`${API}/entries/${p.id}`, { urgency: key });
    load();
  };
  const startPrayer = async (p) => {
    await axios.put(`${API}/entries/${p.id}`, { started_at: new Date().toISOString() });
    toast.success("Prayer started — be present with Him");
    load();
  };
  const markAnswered = async (p) => {
    await axios.put(`${API}/entries/${p.id}`, { answer_status: "answered" });
    toast.success("Praise God — moved to your Archive");
    load();
  };

  const byUrgency = (key) => prayers.filter((p) => (p.urgency || "medium") === key);
  const byWaiting = [...prayers].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));

  const cardProps = { onUrgency: setUrgency, onStart: startPrayer, onAnswered: markAnswered, onPrayNow: setFocusEntry };

  return (
    <div className="min-h-screen bg-clay-app">
      <header className="sticky top-0 z-30 overflow-hidden border-b border-[#E5E1D8] bg-clay-app/90 backdrop-blur-xl">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(600px circle at 92% -40%, rgba(196,164,124,0.18), transparent 60%)" }} />
        <div className="relative mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-4 px-6 py-5 lg:px-10">
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.25em] text-ink-muted">Awaiting & In Progress</p>
            <h1 className="font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">Priorities</h1>
          </div>
          <AppNav />
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8 lg:px-10">
        {loading ? (
          <p className="font-serif text-xl italic text-ink-soft">Gathering your prayers…</p>
        ) : prayers.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E5E1D8] p-12 text-center" data-testid="priorities-empty">
            <ListChecks className="mx-auto mb-4 h-8 w-8 text-ink-muted" />
            <p className="font-serif text-2xl text-ink">No prayers awaiting</p>
            <p className="mt-2 font-sans text-sm text-ink-muted">Every prayer has been answered, or none are set yet. Add prayers from “This week”.</p>
          </div>
        ) : (
          <>
            {/* Sort toggle */}
            <div className="mb-8 flex items-center gap-2" data-testid="sort-toggle">
              <span className="mr-1 font-sans text-xs uppercase tracking-[0.1em] text-ink-muted">Sort</span>
              <button
                onClick={() => setSortMode("urgency")}
                data-testid="sort-urgency"
                className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-sans text-sm transition-colors duration-300 ${
                  sortMode === "urgency" ? "border-brand bg-brand text-white" : "border-[#E5E1D8] bg-white text-ink-soft hover:bg-clay-panel"
                }`}
              >
                <Flame className="h-4 w-4" /> By urgency
              </button>
              <button
                onClick={() => setSortMode("waiting")}
                data-testid="sort-waiting"
                className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-sans text-sm transition-colors duration-300 ${
                  sortMode === "waiting" ? "border-brand bg-brand text-white" : "border-[#E5E1D8] bg-white text-ink-soft hover:bg-clay-panel"
                }`}
              >
                <Hourglass className="h-4 w-4" /> Longest waiting
              </button>
            </div>

            {sortMode === "urgency" ? (
              <div className="space-y-10" data-testid="priorities-list">
                {GROUPS.map((group) => {
                  const items = byUrgency(group.key);
                  if (items.length === 0) return null;
                  const meta = urgencyMeta(group.key);
                  return (
                    <section key={group.key} data-testid={`priority-group-${group.key}`}>
                      <div className="mb-4 flex items-baseline gap-3">
                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: meta.color }} />
                        <h2 className="font-serif text-2xl text-ink">{group.label}</h2>
                        <span className="font-sans text-sm text-ink-muted">{group.hint} · {items.length}</span>
                      </div>
                      <div className="space-y-3">
                        {items.map((p) => (
                          <PriorityCard key={p.id} p={p} meta={meta} {...cardProps} />
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-3" data-testid="priorities-list">
                {byWaiting.map((p) => (
                  <PriorityCard key={p.id} p={p} meta={urgencyMeta(p.urgency || "medium")} {...cardProps} />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {focusEntry && (
        <FocusMode entry={focusEntry} onClose={() => setFocusEntry(null)} onSaved={load} />
      )}
    </div>
  );
}
